/**
 * 낡은 상자 — 뚜껑이 열리는 보관 상자.
 */

import * as THREE from 'three';
import { bevelBox, shellBox, type MeshWriter } from './kernel';
import { paintGeometry, type Piece } from './pieces';

/* ------------------------------------------------------------------ */
/* 공용 부품                                                            */
/* ------------------------------------------------------------------ */

/** 상자 하나 — saholStory.ts 와 같은 축약형. bevelBox + translate + add. */
function box(
  w: MeshWriter,
  slot: Parameters<MeshWriter['add']>[1],
  sx: number,
  sy: number,
  sz: number,
  x: number,
  y: number,
  z: number,
  bevel = 0.003,
  uv = 0.7,
) {
  const g = bevelBox(sx, sy, sz, bevel);
  g.translate(x, y, z);
  w.add(g, slot, uv);
}

/* ------------------------------------------------------------------ */
/* 절차 텍스처                                                          */
/* ------------------------------------------------------------------ */

function mulberry32(seed: number) {
  let a = seed | 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function canvas(w: number, h: number) {
  const cv = document.createElement('canvas');
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext('2d')!;
  return { cv, ctx };
}

function toTexture(cv: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** 사진 — 두 아기가 나란히 있는 오래된 인화지. 실제 사진을 흉내 내는 대신 세피아
 *  톤 + 두 개의 둥근 실루엣만으로 '아기 둘'이라는 사실만 남긴다(부적 텍스처와
 *  같은 절차: 사실적 묘사가 아니라 구성 규칙으로 정체를 말한다). */
function babyPhotoTexture(): THREE.CanvasTexture {
  const w = 256;
  const h = 192;
  const { cv, ctx } = canvas(w, h);
  const rnd = mulberry32(21);
  ctx.fillStyle = '#e9e2cf';
  ctx.fillRect(0, 0, w, h);
  const bx = w * 0.06;
  const by = h * 0.07;
  const bw = w * 0.88;
  const bh = h * 0.8;
  const grad = ctx.createLinearGradient(0, by, 0, by + bh);
  grad.addColorStop(0, '#c9a876');
  grad.addColorStop(1, '#8a6a44');
  ctx.fillStyle = grad;
  ctx.fillRect(bx, by, bw, bh);
  for (let i = 0; i < 40; i++) {
    ctx.fillStyle = `rgba(50,36,20,${rnd() * 0.1})`;
    ctx.beginPath();
    ctx.arc(bx + rnd() * bw, by + rnd() * bh, rnd() * 10 + 2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = 'rgba(40,28,16,0.55)';
  for (const cx of [bx + bw * 0.36, bx + bw * 0.64]) {
    const cy = by + bh * 0.42;
    ctx.beginPath();
    ctx.ellipse(cx, cy, bw * 0.07, bh * 0.1, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(cx - bw * 0.11, by + bh * 0.9);
    ctx.quadraticCurveTo(cx, cy + bh * 0.05, cx + bw * 0.11, by + bh * 0.9);
    ctx.closePath();
    ctx.fill();
  }
  return toTexture(cv);
}

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

export const KEEPSAKE_PIECES: Piece[] = [

  {
    id: 'baenaet_sangja',
    glb: '/models/sahol/baenaet_sangja',
    name: '낡은 상자',
    collection: 'sahol',
    category: '안방',
    note:
      '장롱 위칸에서 나온 상자. 배냇저고리와 사진 사이에 손전등 배터리가 놓여 ' +
      '있다 — 아기 물건을 뒤지다 그것이 나오는 순간이 이 상자의 전부라, 배터리는 ' +
      '천 더미에 묻히지 않는 자리(상자 앞쪽, 가장 먼저 보이는 자리)에 뒀다. ' +
      '뚜껑은 suitcase(saholStory.ts)와 같은 힌지 축 회전 방식으로 열어 둔다.',
    dims: { w: 0.42, d: 0.3, h: 0.18, lidAngle: 0.9 },
    /* GLB 단일 메시라 뚜껑을 떼어낼 수 없다 — 상자를 통째로 들어 옮기는 것과
     * 곁가지로 꺼내 둔 손전등 배터리가 이 물건의 상호작용이다 (2026-09-16) */
    openable: [
      { part: '상자', node: '*', slide: [0, 0.06, 0] },
      { part: '손전등 배터리', slide: [0, 0.04, 0] },
    ],
    materials() {
      return {
        // fabric 슬롯 기본색(#4d5262, 무복 톤)은 낡은 흰 배냇저고리에 안 맞아 덮어쓴다
        fabric: new THREE.MeshStandardMaterial({ color: '#e8e1d0', roughness: 0.88 }),
        painted: new THREE.MeshStandardMaterial({
          map: babyPhotoTexture(),
          roughness: 0.7,
          side: THREE.DoubleSide,
        }),
      };
    },
    build(w, d) {
      const t = 0.01; // 판 두께
      const bodyH = d.h * 0.75;
      const lidH = d.h - bodyH;
      const hd = d.d / 2;

      w.part('몸통');
      // 위(+y)만 열린 통 — shellBox 로 짜야 판 하나를 빼먹는 실수가 안 생긴다
      for (const g of shellBox(d.w, bodyH, d.d, t, ['+y'])) w.add(g, 'oak', 0.6);

      w.part('뚜껑');
      // 힌지는 몸통 뒷벽 상단(0, bodyH, +hd). 뚜껑 껍데기를 힌지 기준 로컬로
      // 옮긴 뒤 통째로 젖힌다 — suitcase 피스와 동일한 수법.
      for (const g of shellBox(d.w, lidH, d.d, t, ['-y'])) {
        g.translate(0, 0, -d.d / 2);
        g.rotateX(d.lidAngle);
        g.translate(0, bodyH, hd);
        w.add(g, 'oak', 0.6);
      }

      w.part('경첩');
      for (const sx of [-0.12, 0.12]) {
        box(w, 'iron', 0.045, 0.018, 0.02, sx, bodyH - 0.002, hd - 0.01, 0.002, 0.15);
      }

      w.part('배냇저고리');
      // 개킨 천 — 판 하나로 두면 헝겊이 아니라 상자로 보인다. 살짝 다른 각도로
      // 겹쳐야(suitcase.ts의 '삐져나온 옷'과 같은 수법) 개킨 것으로 읽힌다.
      for (const [ox, oz, rx, rz, sw, sd] of [
        [-d.w * 0.16, d.d * 0.14, -0.06, 0.05, 0.16, 0.13],
        [-d.w * 0.1, d.d * 0.2, 0.04, -0.08, 0.14, 0.1],
        [-d.w * 0.18, d.d * 0.08, -0.1, 0.1, 0.11, 0.09],
      ] as const) {
        const fold = bevelBox(sw, 0.018, sd, 0.006);
        fold.rotateX(rx);
        fold.rotateZ(rz);
        fold.translate(ox, t + 0.014, oz);
        w.add(fold, 'fabric', 0.5);
      }

      w.part('사진');
      // 진짜 두께가 없는 것 — thinSurface 로 닫힘 검사에서 뺀다
      w.thinSurface(() => {
        const photo = new THREE.PlaneGeometry(0.085, 0.06);
        photo.rotateX(-Math.PI / 2);
        photo.rotateZ(0.15);
        photo.translate(d.w * 0.02, t + 0.024, d.d * 0.02);
        w.add(photo, 'painted', 0);
      });

      w.part('손전등 배터리');
      // 열린 상자 안 천 위의 배터리 — 이 상자의 핵심이라 가장 먼저 보이는 자리다.
      // glb 조각이라 곁가지로 얹어 집어 드는 여닫이로 쓴다 (2026-09-16). 몸통
      // 바운딩 안쪽이라 fit 이 안 커진다.
      const battR = 0.013;
      const battLen = 0.05;
      const battX = -d.w * 0.16;
      const battZ = -d.d * 0.2;
      w.movable('손전등 배터리', () => {
        const batt = new THREE.CylinderGeometry(battR, battR, battLen, 14);
        batt.rotateZ(Math.PI / 2);
        batt.translate(battX, bodyH * 0.55, battZ);
        paintGeometry(batt, '#a8341f');
        w.add(batt, 'paint', 0.1);
        const cap = new THREE.CylinderGeometry(battR * 0.5, battR * 0.5, 0.006, 10);
        cap.rotateZ(Math.PI / 2);
        cap.translate(battX + battLen / 2, bodyH * 0.55, battZ);
        w.add(cap, 'iron', 0.05);
      });
    },
  },
];
